/**
 * HubSystemDescriptionPage — drafts a SOC 2 / ISAE 3000-style system description
 * from data already recorded in Nimbus, instead of writing it by hand.
 *
 * Standalone Hub page, same pattern as HubInitiativeTracker/HubServiceCatalog
 * (registered at its own top-level route, not nested under HubLayout's category
 * tree — this isn't a document register, it's a generator). This is Hub's first
 * write/generate capability; everything else in Hub today is read-only.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '../../components/PageHeader';
import { documentsApi, systemDescriptionApi } from '../../services/api';
import { useToast } from '../../hooks/useToast';
import { useSwal } from '../../hooks/useSwal';
import { useAuth } from '../../hooks/useAuth';

interface ReportSummary {
  reportId: string;
  generatedAt: string;
  periodStart: string;
  periodEnd: string;
  contextTier: 'low' | 'high';
  status: 'generating' | 'complete' | 'failed';
}

interface ReportSection {
  title: string;
  content: string;
  sourceRecordCount: number;
  flaggedCount: number;
  generated: boolean;
}

interface FullReport extends ReportSummary {
  sections: Record<string, ReportSection>;
  error?: string;
}

const SECTION_ORDER = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12', '13', '14', '15'];

const todayIso = () => new Date().toISOString().slice(0, 10);
const sixMonthsAgoIso = () => {
  const d = new Date();
  d.setMonth(d.getMonth() - 6);
  return d.toISOString().slice(0, 10);
};

export const HubSystemDescriptionPage: React.FC = () => {
  const toast = useToast();
  const swal = useSwal();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  // Must match App.tsx's isTenantAdmin expression verbatim - generating a report is a real
  // Bedrock cost and produces an official compliance document, gated the same way as every
  // other admin-only action (e.g. AiUsagePage). Report viewing/gap analysis below stay open
  // to any authenticated user in the tenant - only the generate action is admin-only.
  const isTenantAdmin = (user?.role || '').toLowerCase() === 'admin';

  const [periodStart, setPeriodStart] = useState(sixMonthsAgoIso());
  const [periodEnd, setPeriodEnd] = useState(todayIso());
  const [contextTier, setContextTier] = useState<'low' | 'high'>('low');
  const [selectedDocIds, setSelectedDocIds] = useState<string[]>([]);
  const [comparePreviousId, setComparePreviousId] = useState('');
  const [viewingReportId, setViewingReportId] = useState<string | null>(null);
  const [gapSummary, setGapSummary] = useState<string | null>(null);

  const { data: reports = [] } = useQuery<ReportSummary[]>({
    queryKey: ['systemDescriptionReports'],
    queryFn: systemDescriptionApi.listReports,
    staleTime: 10_000,
  });

  // High-context document pool: every non-attachment (entityType === 'register')
  // document in the tenant, not just Hub's four curated categories — confirmed
  // this tenant-wide scan already exists and is used elsewhere ("Maintenance Tasks").
  const { data: allDocuments = [] } = useQuery<any[]>({
    queryKey: ['allDocumentsForSystemDescription'],
    queryFn: documentsApi.listAll,
    enabled: contextTier === 'high',
  });
  const pickableDocs = allDocuments.filter((d) => d.entityType === 'register');

  const generateMutation = useMutation({
    mutationFn: () => systemDescriptionApi.generate({
      periodStart,
      periodEnd,
      contextTier,
      sourceDocumentIds: contextTier === 'high' ? selectedDocIds : undefined,
    }),
    onSuccess: (data) => {
      toast.success('Generating report — this runs in the background.');
      setViewingReportId(data.reportId);
      queryClient.invalidateQueries({ queryKey: ['systemDescriptionReports'] });
    },
    onError: (e: Error) => toast.error(`Failed to start generation: ${e.message}`),
  });

  const deleteMutation = useMutation({
    mutationFn: (reportId: string) => systemDescriptionApi.deleteReport(reportId),
    onSuccess: (_, reportId) => {
      toast.success('Report deleted.');
      if (viewingReportId === reportId) setViewingReportId(null);
      queryClient.invalidateQueries({ queryKey: ['systemDescriptionReports'] });
    },
    onError: (e: Error) => toast.error(`Failed to delete report: ${e.message}`),
  });

  const { data: viewingReport } = useQuery<FullReport>({
    queryKey: ['systemDescriptionReport', viewingReportId],
    queryFn: () => systemDescriptionApi.getReport(viewingReportId!),
    enabled: !!viewingReportId,
    refetchInterval: (query) => (query.state.data?.status === 'generating' ? 2000 : false),
  });

  useEffect(() => {
    if (viewingReport?.status === 'complete' || viewingReport?.status === 'failed') {
      queryClient.invalidateQueries({ queryKey: ['systemDescriptionReports'] });
    }
  }, [viewingReport?.status, queryClient]);

  const gapMutation = useMutation({
    mutationFn: () => systemDescriptionApi.gapAnalysis(viewingReportId!, comparePreviousId),
    onSuccess: (data) => setGapSummary(data.summary),
    onError: (e: Error) => toast.error(`Gap analysis failed: ${e.message}`),
  });

  const toggleDoc = (docId: string) => {
    setSelectedDocIds((prev) => (prev.includes(docId) ? prev.filter((id) => id !== docId) : [...prev, docId]));
  };

  return (
    <div className="container-xxl flex-grow-1 container-p-y np-page">
      <nav className="np-crumb mb-2">
        <Link to="/hub">Hub</Link>
        <span className="np-crumb-sep">›</span>
        <span className="np-crumb-current">System Description</span>
      </nav>

      <PageHeader
        icon="ri-file-text-line"
        title="System Description Generator"
        subtitle="Draft a SOC 2 / ISAE 3000-style system description from data already in Nimbus."
      />

      {isTenantAdmin && (
      <div className="np-card">
        <div className="np-card-head"><h2><i className="ri-add-box-line"></i> New Report</h2></div>
        <div className="np-card-body">
          <div className="row g-3 mb-3">
            <div className="col-12 col-md-6 np-field">
              <label>Period start</label>
              <input type="date" className="np-input" value={periodStart} onChange={(e) => setPeriodStart(e.target.value)} />
            </div>
            <div className="col-12 col-md-6 np-field">
              <label>Period end</label>
              <input type="date" className="np-input" value={periodEnd} onChange={(e) => setPeriodEnd(e.target.value)} />
            </div>
          </div>

          <div className="mb-3 np-field">
            <label>Context level</label>
            <div className="d-flex gap-2">
              <button
                type="button"
                className={`np-btn np-btn-sm ${contextTier === 'low' ? 'np-btn-primary' : 'np-btn-ghost'}`}
                onClick={() => setContextTier('low')}
              >
                Low context
              </button>
              <button
                type="button"
                className={`np-btn np-btn-sm ${contextTier === 'high' ? 'np-btn-primary' : 'np-btn-ghost'}`}
                onClick={() => setContextTier('high')}
              >
                High context
              </button>
            </div>
            <p className="np-hint mb-0 mt-1">
              {contextTier === 'low'
                ? "Governance only — policies and processes describe what exists and how it's controlled."
                : 'Expands into selected documentation. Choose which documents to include below.'}
            </p>
          </div>

          {contextTier === 'high' && (
            <div className="mb-3 np-card" style={{ background: 'var(--np-surface-2, #f4f6fb)' }}>
              <div className="np-card-body np-field">
                <label>Documents to include</label>
                {pickableDocs.length === 0 && <p className="np-sub mb-0">No policy/procedure documents found for this tenant.</p>}
                {pickableDocs.map((doc) => (
                  <label key={doc.documentId} className="d-flex align-items-center gap-2 mb-1" style={{ fontSize: '0.85rem' }}>
                    <input
                      type="checkbox"
                      checked={selectedDocIds.includes(doc.documentId)}
                      onChange={() => toggleDoc(doc.documentId)}
                    />
                    {doc.title || 'Untitled'}
                  </label>
                ))}
              </div>
            </div>
          )}

          <div className="d-flex justify-content-end">
            <button
              type="button"
              className="np-btn np-btn-primary"
              disabled={generateMutation.isPending}
              onClick={() => generateMutation.mutate()}
            >
              <i className="ri-flashlight-line"></i>
              {generateMutation.isPending ? 'Starting…' : 'Generate report'}
            </button>
          </div>
        </div>
      </div>
      )}

      <div className="np-card">
        <div className="np-card-head"><h2>Previous Reports</h2></div>
        <div className="np-card-body p-0">
          <table className="np-table">
            <thead>
              <tr><th>Generated</th><th>Period</th><th>Context</th><th>Status</th><th></th></tr>
            </thead>
            <tbody>
              {reports.length === 0 && (
                <tr><td colSpan={5} className="np-sub text-center py-3">No reports generated yet.</td></tr>
              )}
              {reports.map((r) => (
                <tr key={r.reportId}>
                  <td className="np-tabular">{new Date(r.generatedAt).toLocaleDateString()}</td>
                  <td>{r.periodStart} – {r.periodEnd}</td>
                  <td><span className={`np-chip np-tone-${r.contextTier === 'high' ? 'primary' : 'neutral'}`}>{r.contextTier === 'high' ? 'High' : 'Low'}</span></td>
                  <td>
                    <span className={`np-chip np-tone-${r.status === 'complete' ? 'success' : r.status === 'failed' ? 'danger' : 'warning'}`}>
                      {r.status}
                    </span>
                  </td>
                  <td>
                    <div className="d-flex gap-1">
                      <button
                        type="button"
                        className="np-icon-btn"
                        title="View"
                        onClick={() => setViewingReportId(r.reportId)}
                      >
                        <i className="ri-eye-line"></i>
                      </button>
                      <button
                        type="button"
                        className="np-icon-btn np-icon-btn-danger"
                        title="Delete"
                        disabled={deleteMutation.isPending}
                        onClick={async () => {
                          const confirmed = await swal.confirmDelete(`${r.periodStart} – ${r.periodEnd}`);
                          if (confirmed) deleteMutation.mutate(r.reportId);
                        }}
                      >
                        <i className="ri-delete-bin-line"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {viewingReport && (
        <div className="np-card">
          <div className="np-card-head">
            <h2><i className="ri-file-text-line"></i> {viewingReport.periodStart} to {viewingReport.periodEnd}</h2>
            <span className={`np-chip np-tone-${viewingReport.status === 'complete' ? 'success' : viewingReport.status === 'failed' ? 'danger' : 'warning'}`}>
              {viewingReport.status}
            </span>
          </div>
          <div className="np-card-body">
            {viewingReport.status === 'generating' && (
              <div className="d-flex align-items-center gap-2">
                <div className="spinner-border spinner-border-sm text-primary" role="status" />
                <span className="np-sub">Generating — sections appear as each group finishes.</span>
              </div>
            )}
            {viewingReport.status === 'failed' && (
              <div className="np-banner np-tone-danger">
                <i className="ri-error-warning-line"></i>
                <span>Generation failed: {viewingReport.error || 'Unknown error'}</span>
              </div>
            )}

            {SECTION_ORDER.filter((k) => viewingReport.sections?.[k]).map((key) => {
              const section = viewingReport.sections[key];
              return (
                <div key={key} className="mb-4">
                  <h3 style={{ fontSize: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                    {key}. {section.title}
                    {section.generated && section.sourceRecordCount > 0 && (
                      <span className="np-chip np-tone-info">Based on {section.sourceRecordCount} real record{section.sourceRecordCount === 1 ? '' : 's'}</span>
                    )}
                    {section.generated && section.flaggedCount > 0 && (
                      <span className="np-chip np-tone-warning" title="A reference in this section could not be verified against real data - review before relying on it.">
                        {section.flaggedCount} unverified reference{section.flaggedCount === 1 ? '' : 's'}
                      </span>
                    )}
                  </h3>
                  <div className="np-rich-text ql-editor">
                    <ReactMarkdown>{section.content}</ReactMarkdown>
                  </div>
                </div>
              );
            })}

            {viewingReport.status === 'complete' && reports.length > 1 && (
              <div className="np-card" style={{ background: 'var(--np-surface-2, #f4f6fb)' }}>
                <div className="np-card-body np-field">
                  <label>Compare against a previous report</label>
                  <div className="d-flex gap-2">
                    <select
                      className="np-select"
                      value={comparePreviousId}
                      onChange={(e) => setComparePreviousId(e.target.value)}
                    >
                      <option value="">Select a report…</option>
                      {reports.filter((r) => r.reportId !== viewingReport.reportId && r.status === 'complete').map((r) => (
                        <option key={r.reportId} value={r.reportId}>{r.periodStart} – {r.periodEnd}</option>
                      ))}
                    </select>
                    <button
                      type="button"
                      className="np-btn np-btn-ghost np-btn-sm"
                      disabled={!comparePreviousId || gapMutation.isPending}
                      onClick={() => gapMutation.mutate()}
                    >
                      {gapMutation.isPending ? 'Comparing…' : 'What changed?'}
                    </button>
                  </div>
                  {gapSummary && (
                    <div className="np-banner np-tone-warning mt-3">
                      <i className="ri-git-branch-line"></i>
                      <div className="np-rich-text ql-editor" style={{ flex: 1 }}>
                        <ReactMarkdown>{gapSummary}</ReactMarkdown>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default HubSystemDescriptionPage;
